import React, { useMemo, useState } from "react";
import { Alert, Button, Input, Table } from "reactstrap";

const RANGE_SUPPORTED_REPORT_IDS = new Set([
  "clientes",
  "cuentas",
  "entidades",
  "fondos",
  "instrumentos",
  "negociacion",
]);

// Static catalog used to populate the individual-download select before "Get"
// is pressed. Keep ids/names in sync with the API report definitions.
const REPORT_DEFINITIONS = [
  { reportId: "clientes", name: "Clientes" },
  { reportId: "cuentas", name: "Cuentas" },
  { reportId: "entidades", name: "Entidades" },
  { reportId: "instrumentos", name: "Instrumentos" },
  { reportId: "fondos", name: "Fondos" },
  { reportId: "negociacion", name: "Negociacion" },
  { reportId: "inventario", name: "Inventario" },
  { reportId: "cuotapartistas", name: "Cuotapartistas" },
  { reportId: "valorizacion", name: "Valorizacion" },
];

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

const normalizeError = (error) => {
  if (!error) {
    return "Unexpected error";
  }

  if (typeof error === "string") {
    return error;
  }

  if (error.error) {
    return error.error;
  }

  if (error.message) {
    return error.message;
  }

  return "Unexpected error";
};

const normalizeHiddenReportIds = (hiddenReportIds = []) =>
  new Set(hiddenReportIds.map((item) => `${item}`.toLowerCase()));

const StatusBadge = ({ status }) => {
  const background = {
    ready: "#dff5e1",
    empty: "#f7f0d7",
    failed: "#f8d7da",
    pending: "#e9ecef",
    getting: "#e7f1ff",
    downloading: "#e7f1ff",
    done: "#dff5e1",
  }[status] || "#e9ecef";

  const color = {
    ready: "#1b6b2a",
    empty: "#7a5d00",
    failed: "#842029",
    pending: "#495057",
    getting: "#0b5ed7",
    downloading: "#0b5ed7",
    done: "#1b6b2a",
  }[status] || "#495057";

  return (
    <span
      style={{
        background,
        borderRadius: 4,
        color,
        display: "inline-block",
        fontSize: 12,
        fontWeight: 600,
        padding: "2px 8px",
        textTransform: "capitalize",
      }}
    >
      {status}
    </span>
  );
};

const DailyLegalReportsWorkspace = ({ hiddenReportIds, services, title }) => {
  const [periodo, setPeriodo] = useState(formatDateInput(new Date()));
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [catalog, setCatalog] = useState(null);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingById, setDownloadingById] = useState({});
  const [selectedReportId, setSelectedReportId] = useState("");
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const hiddenReportIdSet = useMemo(() => normalizeHiddenReportIds(hiddenReportIds), [hiddenReportIds]);
  const hasRange = Boolean(desde || hasta);

  // Reports available in the individual-download select, independent of "Get".
  const selectableReports = useMemo(
    () =>
      REPORT_DEFINITIONS.filter((item) => {
        const reportId = `${item.reportId}`.toLowerCase();
        if (hiddenReportIdSet.has(reportId)) {
          return false;
        }

        if (hasRange && !RANGE_SUPPORTED_REPORT_IDS.has(reportId)) {
          return false;
        }

        return true;
      }),
    [hasRange, hiddenReportIdSet]
  );

  const isSelectionValid =
    !!selectedReportId && selectableReports.some((item) => item.reportId === selectedReportId);

  const currentFilters = useMemo(
    () => ({
      periodo,
      desde: desde || undefined,
      hasta: hasta || undefined,
    }),
    [desde, hasta, periodo]
  );

  const visibleReports = useMemo(() => {
    if (!catalog || !catalog.reports) {
      return [];
    }

    return catalog.reports.filter((item) => {
      const reportId = `${item.reportId}`.toLowerCase();
      if (hiddenReportIdSet.has(reportId)) {
        return false;
      }

      if (hasRange && !RANGE_SUPPORTED_REPORT_IDS.has(reportId)) {
        return false;
      }

      return true;
    });
  }, [catalog, hasRange, hiddenReportIdSet]);

  const visibleSummary = useMemo(() => {
    if (!catalog) {
      return null;
    }

    return {
      ready: visibleReports.filter((item) => item.status === "ready").length,
      empty: visibleReports.filter((item) => item.status === "empty").length,
      failed: visibleReports.filter((item) => item.status === "failed").length,
    };
  }, [catalog, visibleReports]);

  const updateRowState = (reportId, changes) => {
    setCatalog((current) => {
      if (!current || !current.reports) {
        return current;
      }

      return {
        ...current,
        reports: current.reports.map((item) =>
          item.reportId === reportId
            ? {
                ...item,
                ...changes,
              }
            : item
        ),
      };
    });
  };

  const handleGet = async () => {
    setLoadingCatalog(true);
    setMessage(null);
    setError(null);

    try {
      const response = await services.getCatalog(currentFilters);
      setCatalog(response);
    } catch (loadError) {
      setError(normalizeError(loadError));
    } finally {
      setLoadingCatalog(false);
    }
  };

  const handleDownloadReport = async (reportId) => {
    setDownloadingById((current) => ({ ...current, [reportId]: true }));
    setMessage(null);
    setError(null);
    updateRowState(reportId, { status: "downloading" });

    try {
      const response = await services.downloadReport({
        reportId,
        ...currentFilters,
      });
      downloadBlob(response.blob, response.fileName);
      updateRowState(reportId, { status: "done" });
      setMessage("Report downloaded");
    } catch (downloadError) {
      updateRowState(reportId, { status: "failed", error: normalizeError(downloadError) });
      setError(normalizeError(downloadError));
    } finally {
      setDownloadingById((current) => ({ ...current, [reportId]: false }));
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    setMessage(null);
    setError(null);

    try {
      const response = await services.downloadAll(currentFilters);
      downloadBlob(response.blob, response.fileName);
      setMessage("ZIP downloaded");
    } catch (downloadError) {
      setError(normalizeError(downloadError));
    } finally {
      setDownloadingAll(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ marginBottom: 8 }}>{title || "Daily Legal Reports"}</h2>
        <p style={{ color: "#6c757d", marginBottom: 0 }}>
          Download individual CSV reports or the full ZIP package when you are ready.
        </p>
      </div>

      {error ? <Alert color="danger">{error}</Alert> : null}
      {message ? <Alert color="success">{message}</Alert> : null}

      <div
        style={{
          alignItems: "end",
          borderBottom: "1px solid #dee2e6",
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 24,
          paddingBottom: 16,
        }}
      >
        <div style={{ minWidth: 200 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Period</label>
          <Input type="date" value={periodo} onChange={(event) => setPeriodo(event.target.value)} />
        </div>
        <div style={{ minWidth: 200 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>From</label>
          <Input type="date" value={desde} onChange={(event) => setDesde(event.target.value)} />
        </div>
        <div style={{ minWidth: 200 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>To</label>
          <Input type="date" value={hasta} onChange={(event) => setHasta(event.target.value)} />
        </div>
        <Button color="secondary" onClick={handleGet} disabled={loadingCatalog || downloadingAll}>
          {loadingCatalog ? "Getting..." : "Get"}
        </Button>
        <Button color="primary" onClick={handleDownloadAll} disabled={downloadingAll || loadingCatalog || !catalog}>
          {downloadingAll ? "Getting reports..." : "Download All"}
        </Button>
        {visibleSummary ? (
          <div style={{ color: "#495057", fontSize: 13, marginLeft: "auto" }}>
            Ready: {visibleSummary.ready} | Empty: {visibleSummary.empty} | Failed: {visibleSummary.failed}
          </div>
        ) : null}
      </div>

      <div
        style={{
          alignItems: "end",
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ minWidth: 260 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Download individual report
          </label>
          <Input
            type="select"
            value={selectedReportId}
            onChange={(event) => setSelectedReportId(event.target.value)}
          >
            <option value="">Select a report...</option>
            {selectableReports.map((item) => (
              <option key={item.reportId} value={item.reportId}>
                {item.name}
              </option>
            ))}
          </Input>
        </div>
        <Button
          color="primary"
          outline
          onClick={() => handleDownloadReport(selectedReportId)}
          disabled={
            !isSelectionValid ||
            !!downloadingById[selectedReportId] ||
            downloadingAll
          }
        >
          {downloadingById[selectedReportId] ? "Getting..." : "Download"}
        </Button>
      </div>

      {hasRange ? (
        <div style={{ color: "#6c757d", fontSize: 13, marginBottom: 16 }}>
          Reports without range support are hidden while `From` or `To` is set.
        </div>
      ) : null}

      <div style={{ marginBottom: 24 }}>
        <h5>Reports</h5>
        <Table hover responsive size="sm">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Rows</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!catalog ? (
              <tr>
                <td colSpan="4" style={{ color: "#6c757d", textAlign: "center" }}>
                  Choose your filters and press Get to load the reports.
                </td>
              </tr>
            ) : !visibleReports.length ? (
              <tr>
                <td colSpan="4" style={{ textAlign: "center" }}>
                  {loadingCatalog ? "Loading reports..." : "No reports available"}
                </td>
              </tr>
            ) : (
              visibleReports.map((item) => (
                <tr key={item.reportId}>
                  <td>{item.name}</td>
                  <td>
                    <StatusBadge status={item.status} />
                  </td>
                  <td>{item.rowCount === null || item.rowCount === undefined ? "-" : item.rowCount}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <Button
                      color="link"
                      size="sm"
                      onClick={() => handleDownloadReport(item.reportId)}
                      disabled={!!downloadingById[item.reportId] || downloadingAll}
                    >
                      {downloadingById[item.reportId] ? "Getting..." : "Download"}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default DailyLegalReportsWorkspace;
